/**
 * The Medium tier's light for one floor (lifted from the dungeon-floors
 * prototype's `dungeon-lighting.js`): the fixtures of `lighting-plan.ts` as
 * geometry (iron A-frame braziers, wall sconces, hanging lanterns and the
 * cracks shafts fall from, all instances of one batch like the markers),
 * their flickering flames, the flame coronas, the warm ground pools with a
 * contact shadow, the seeded dust decals and the light shafts; and the baked
 * volume they light the floor with (`radiance.ts`). Static: no dynamic
 * lights, no particles. The one directional light with its 1024 shadow map
 * and the hemisphere fill belong to the view (`medium.ts`). Everything here
 * is built with the floor and disposed with it; fixtures on unexplored tiles
 * are scaled to zero.
 */
import type { Theme } from '@ab/content-schema';
import type { Floor } from '@ab/game-core';
import * as THREE from 'three';
import { type Shafts, createShafts } from './atmosphere.ts';
import { presentation } from './constants.ts';
import type { ExplorationMask } from './fog.ts';
import { type Glow, type GlowFixture, createGlow } from './glow.ts';
import { seededRandom, tileIndex } from './grid.ts';
import type { LightingPlan } from './lighting-plan.ts';
import { addShaderHook } from './material-hooks.ts';
import { type Bake, createBake } from './radiance.ts';

export type FloorLighting = {
  readonly group: THREE.Group;
  readonly bake: Bake;
  readonly plan: LightingPlan;
  updateFog(seen: ReadonlySet<number>): void;
  update(time: number, depth: THREE.Texture | null, camera: THREE.PerspectiveCamera): void;
  /** Frees the GPU copies (tier Off); the next Medium frame uploads them again. */
  release(): void;
  dispose(): void;
};

const b = presentation.medium.brazier;

/** A static asymmetrical tongue: a broad rounded body and a narrow leaning tip. */
const flameGeometry = (): THREE.BufferGeometry => {
  const points: number[] = [];
  const indices: number[] = [];
  const n = b.flameSides;
  b.flameRings.forEach(([y, r, lean]) => {
    for (let i = 0; i < n; i += 1) {
      const a = (i / n) * Math.PI * 2;
      points.push(Math.cos(a) * r + lean, y, Math.sin(a) * r);
    }
  });
  for (let j = 0; j < b.flameRings.length - 1; j += 1) {
    for (let i = 0; i < n; i += 1) {
      const p = j * n + i;
      const q = j * n + ((i + 1) % n);
      indices.push(p, q, p + n, q, q + n, p + n);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
  g.setIndex(indices);
  g.computeVertexNormals();
  return g;
};

const flickerVertex = `
#ifdef USE_INSTANCING
float flamePhase=fract(sin(dot(instanceMatrix[3].xz,vec2(12.9898,78.233)))*43758.5453)*6.2831853;
float flameWave=sin(flameTime*8.1+flamePhase)*.6+sin(flameTime*19.3+flamePhase*1.7)*.4;
transformed.y*=1.+.09*flameWave;transformed.x+=transformed.y*transformed.y*.12*sin(flameTime*5.3+flamePhase);
#endif`;

const poolVertex = 'varying vec2 vUv;varying vec3 vWorld,vColor;void main(){vUv=uv;vColor=instanceColor;vec4 wp=modelMatrix*instanceMatrix*vec4(position,1.);vWorld=wp.xyz;gl_Position=projectionMatrix*viewMatrix*wp;}';
/** Seen floor tiles only: the exploration mask's R (explored) without its G (rock). */
const visibility = 'uniform sampler2D visibility;uniform vec2 floorSize;float tileVisible(vec3 w){vec2 tile=floor(vec2(w.x,-w.z)+.5);vec4 m=texture2D(visibility,(tile+.5)/floorSize);return m.r*(1.-m.g);}';
const poolFragment = `${visibility}varying vec2 vUv;varying vec3 vWorld,vColor;
void main(){float vis=tileVisible(vWorld);
  float r=length(vUv-.5)*2.;float noise=fract(sin(dot(floor(vWorld.xz*28.),vec2(12.9898,78.233)))*43758.5453);
  float pool=exp(-r*r*5.)*(1.-smoothstep(.65,1.,r))*vis;pool*=.9+.1*noise;
  float contact=(1.-smoothstep(.015,.07,r))*vis;
  gl_FragColor=vec4(vColor*pool*.045,mix(1.,1.+pool*.12-contact*.48,vis));}`;
const dustFragment = `${visibility}uniform vec3 dustTint;uniform float dustStrength;varying vec2 vUv;varying vec3 vWorld,vColor;
void main(){float vis=tileVisible(vWorld);
  vec2 p=floor(vWorld.xz*30.);float noise=fract(sin(dot(p,vec2(43.7,91.23)))*38171.31);float radial=1.-smoothstep(.18,.5,length(vUv-.5));float mark=radial*step(.36,noise)*vis;
  gl_FragColor=vec4(dustTint*mark,1.-mark*dustStrength);}`;

/** Ground decals: rgb added, alpha multiplies what is below (neutral is (0,0,0,1)). */
const decalMaterial = (fragmentShader: string, uniforms: Record<string, THREE.IUniform>): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms,
    vertexShader: poolVertex,
    fragmentShader,
    transparent: true,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -2,
    blending: THREE.CustomBlending,
    blendSrc: THREE.OneFactor,
    blendDst: THREE.SrcAlphaFactor,
    blendSrcAlpha: THREE.ZeroFactor,
    blendDstAlpha: THREE.OneFactor,
  });

export const buildLighting = (floor: Floor, theme: Theme, plan: LightingPlan, mask: ExplorationMask, seed: number): FloorLighting => {
  const rules = theme.lightRules;
  const pct = (v: number): number => v / 100;
  const group = new THREE.Group();
  group.name = 'medium-lighting';
  const bake = createBake(floor, plan.sources, theme, mask);
  const cube = new THREE.BoxGeometry(1, 1, 1);
  const flames = flameGeometry();
  const disc = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
  const postMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
  const flameMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false });
  const flameTime = { value: 0 };
  addShaderHook(flameMaterial, { key: 'flame-flicker', uniforms: { flameTime }, vertexHeader: 'uniform float flameTime;', vertex: [{ chunk: 'begin_vertex', after: flickerVertex }] });
  const { torches, lanterns, shafts: shaftPlans } = plan;
  const posts = new THREE.InstancedMesh(cube, postMaterial, Math.max(1, torches.length * b.parts + lanterns.length * b.lanternParts + shaftPlans.length));
  posts.name = 'medium-fixtures';
  const flameMesh = new THREE.InstancedMesh(flames, flameMaterial, Math.max(1, (torches.length + lanterns.length) * 2));
  flameMesh.name = 'medium-flames';
  const flameCore = new THREE.Color(rules.flameCore);
  const glowFixtures: GlowFixture[] = [
    ...torches.map((t): GlowFixture => ({ x: t.x, y: b.glowY, z: t.z, tile: t.tile, color: t.color.clone().lerp(flameCore, b.torchGlowCore), scale: 1 })),
    ...plan.markerLights.filter((t) => t.kind === 'exit').map((t): GlowFixture => ({ x: t.x, y: t.height, z: t.z + b.exitGlowForward, tile: t.tile, color: t.color.clone(), scale: b.exitGlowScale })),
    ...lanterns.map((t): GlowFixture => ({ x: t.x, y: t.height + b.lanternGlowLift, z: t.z, tile: t.tile, color: t.color.clone().lerp(flameCore, b.lanternGlowCore), scale: b.lanternGlowScale })),
  ];
  const glow: Glow = createGlow(glowFixtures, pct(rules.glowGainPercent), pct(rules.glowCorePercent), pct(rules.glowRadiusPercent));
  const visibilityUniforms = { visibility: { value: mask.texture }, floorSize: { value: new THREE.Vector2(floor.width, floor.height) } };
  const poolMaterial = decalMaterial(poolFragment, visibilityUniforms);
  const pools = new THREE.InstancedMesh(disc, poolMaterial, Math.max(1, torches.length));
  pools.name = 'medium-pools';
  pools.count = torches.length;
  const random = seededRandom((seed ^ presentation.medium.decals.salt) | 0);
  const dustTiles: number[] = [];
  floor.tiles.forEach((t, i) => {
    if (t === 'floor' && random() < pct(theme.decals.densityPercent)) dustTiles.push(i);
  });
  const dustMaterial = decalMaterial(dustFragment, {
    ...visibilityUniforms,
    dustTint: { value: new THREE.Color(theme.decals.color).multiplyScalar(presentation.medium.decals.tint) },
    dustStrength: { value: pct(theme.decals.strengthPercent) },
  });
  const dust = new THREE.InstancedMesh(disc, dustMaterial, Math.max(1, dustTiles.length));
  dust.name = 'medium-dust';
  dust.count = dustTiles.length;
  const shafts: Shafts = createShafts(shaftPlans, new THREE.Color(theme.shafts.color), pct(theme.shafts.strengthPercent));
  for (const mesh of [posts, flameMesh, pools, dust]) {
    mesh.frustumCulled = false;
    // The instance colour attribute exists from the start, so every program compiles with it.
    mesh.setColorAt(0, new THREE.Color(1, 1, 1));
  }
  pools.renderOrder = presentation.medium.decals.renderOrder;
  dust.renderOrder = presentation.medium.decals.renderOrder;
  group.add(posts, flameMesh, glow.mesh, pools, dust, shafts.mesh);

  const m = new THREE.Matrix4();
  const v = new THREE.Vector3();
  const s = new THREE.Vector3();
  const identity = new THREE.Quaternion();
  const put = (mesh: THREE.InstancedMesh, i: number, x: number, y: number, z: number, sx: number, sy: number, sz: number, rot: THREE.Quaternion = identity): void => {
    const hidden = sx === 0;
    mesh.setMatrixAt(i, m.compose(v.set(x, y, z), rot, s.set(sx, hidden ? 0 : sy, hidden ? 0 : sz)));
  };
  dustTiles.forEach((tile, i) => {
    const d = presentation.medium.decals;
    put(dust, i, (tile % floor.width) + (random() - 0.5) * d.jitter, d.y, -Math.floor(tile / floor.width) + (random() - 0.5) * d.jitter, d.size[0] + random() * d.size[1], 1, d.size[0] + random() * d.size[1]);
    dust.setColorAt(i, new THREE.Color(1, 1, 1));
  });
  const fixture = new THREE.Color(rules.fixtureColor);
  const ironLight = new THREE.Color(theme.tileMaterials.ramps.iron.at(-1) ?? theme.palette.iron);
  const tint = new THREE.Color();
  const legRotation = new THREE.Quaternion();
  const legDirection = new THREE.Vector3();
  const up = new THREE.Vector3(0, 1, 0);
  const flameOuter = new THREE.Color(rules.flameColor).multiplyScalar(pct(rules.flameGainPercent));
  const flameInner = flameCore.clone().multiplyScalar(pct(rules.flameGainPercent));
  const poolRadius = pct(rules.poolRadiusPercent);
  const sconceReach = pct(rules.sconceReachPercent);
  let released = false;

  const updateFog = (seen: ReadonlySet<number>): void => {
    torches.forEach((t, i) => {
      const k = seen.has(t.tile) ? 1 : 0;
      const n0 = i * b.parts;
      if (t.mount === 'wall') {
        // A wall sconce: a back plate and an arm, the same basket and prongs, no legs.
        const [nx, nz] = t.normal;
        for (let j = 0; j < 3; j += 1) put(posts, n0 + j, t.x, 0, t.z, 0, 0, 0);
        const p = b.sconce;
        put(posts, n0 + 3, t.x - nx * p.back, p.y, t.z - nz * p.back, (nx !== 0 ? p.thin : p.wide) * k, p.height, nx !== 0 ? p.wide : p.thin);
        put(posts, n0 + 4, t.x - nx * p.arm, p.y, t.z - nz * p.arm, (nx !== 0 ? p.armLength : p.armThin) * k, p.armThin, nx !== 0 ? p.armThin : p.armLength);
      } else {
        // An iron A-frame: three splayed legs meeting a centre post, a pale band.
        const l = b.leg;
        for (let j = 0; j < 3; j += 1) {
          const a = (j * Math.PI * 2) / 3 + l.angle;
          const bx = Math.cos(a) * l.spread;
          const bz = Math.sin(a) * l.spread;
          legRotation.setFromUnitVectors(up, legDirection.set(-bx * l.inward, l.top, -bz * l.inward).normalize());
          put(posts, n0 + j, t.x + bx * l.foot, l.top / 2, t.z + bz * l.foot, l.width * k, Math.hypot(bx * l.inward, l.top, bz * l.inward) + l.extra, l.width, legRotation);
        }
        put(posts, n0 + 3, t.x, b.post.y, t.z, b.post.width * k, b.post.height, b.post.width);
        put(posts, n0 + 4, t.x, b.band.y, t.z, b.band.width * k, b.band.height, b.band.width);
      }
      put(posts, n0 + 5, t.x, b.basket.y, t.z, b.basket.width * k, b.basket.height, b.basket.width);
      for (let j = 0; j < 3; j += 1) {
        const a = (j * Math.PI * 2) / 3 + b.prong.angle;
        put(posts, n0 + 6 + j, t.x + Math.cos(a) * b.prong.radius, b.prong.y, t.z + Math.sin(a) * b.prong.radius, b.prong.width * k, b.prong.height, b.prong.width);
      }
      for (let j = 0; j < b.parts; j += 1) {
        if (j < 4) tint.copy(fixture).multiplyScalar(b.shade.legs);
        else if (j === 4) tint.copy(ironLight).multiplyScalar(b.shade.band);
        else if (j === 5) tint.copy(fixture).lerp(t.color, b.shade.basketWarm).multiplyScalar(b.shade.basket);
        else tint.copy(fixture).lerp(t.color, b.shade.prongWarm).multiplyScalar(b.shade.prong);
        posts.setColorAt(n0 + j, tint);
      }
      const f = b.flame;
      put(flameMesh, i * 2, t.x, f.y, t.z, f.outer[0] * k, f.outer[1], f.outer[2]);
      put(flameMesh, i * 2 + 1, t.x, f.y, t.z + f.innerForward, f.inner[0] * k, f.inner[1], f.inner[2]);
      flameMesh.setColorAt(i * 2, flameOuter);
      flameMesh.setColorAt(i * 2 + 1, flameInner);
      const px = t.mount === 'wall' ? t.x + t.normal[0] * sconceReach : t.x;
      const pz = t.mount === 'wall' ? t.z + t.normal[1] * sconceReach : t.z;
      put(pools, i, px, b.poolY, pz, poolRadius * 2 * k, 1, poolRadius * 2);
      pools.setColorAt(i, t.color);
    });
    lanterns.forEach((t, i) => {
      const k = seen.has(t.tile) ? 1 : 0;
      const n = torches.length * b.parts + i * b.lanternParts;
      const fl = (torches.length + i) * 2;
      const l = b.lantern;
      for (let j = 0; j < b.lanternParts; j += 1) posts.setColorAt(n + j, tint.copy(fixture).lerp(t.color, j === 0 ? l.hangerWarm : l.bodyWarm).multiplyScalar(j === 0 ? l.hangerShade : l.bodyShade));
      put(posts, n, t.x, (t.top + t.height + l.capLift) / 2, t.z, l.hanger * k, Math.max(l.minHanger, t.top - t.height - l.capLift), l.hanger);
      put(posts, n + 1, t.x, t.height + l.capLift, t.z, l.cap[0] * k, l.cap[1], l.cap[0]);
      put(posts, n + 2, t.x, t.height - l.baseDrop, t.z, l.base[0] * k, l.base[1], l.base[0]);
      put(flameMesh, fl, t.x, t.height - l.flameDrop, t.z, l.outer[0] * k, l.outer[1], l.outer[2]);
      put(flameMesh, fl + 1, t.x, t.height - l.flameDrop, t.z + l.innerForward, l.inner[0] * k, l.inner[1], l.inner[2]);
      flameMesh.setColorAt(fl, t.color.clone().multiplyScalar(pct(rules.flameGainPercent)));
      flameMesh.setColorAt(fl + 1, flameInner);
    });
    shaftPlans.forEach((p, i) => {
      const n = torches.length * b.parts + lanterns.length * b.lanternParts + i;
      const k = seen.has(tileIndex(floor, Math.round(p.at[0]), Math.round(-p.at[1]))) ? 1 : 0;
      const c = b.crack;
      put(posts, n, p.top[0], p.top[1] + c.lift, p.top[2] - c.back, c.size[0] * k, c.size[1], c.size[2]);
      posts.setColorAt(n, fixture);
    });
    for (const mesh of [posts, flameMesh, pools]) {
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
    if (dust.instanceColor) dust.instanceColor.needsUpdate = true;
    dust.instanceMatrix.needsUpdate = true;
    glow.updateFog((tile) => seen.has(tile));
    shafts.updateFog((x, y) => x >= 0 && y >= 0 && x < floor.width && y < floor.height && seen.has(tileIndex(floor, x, y)));
  };
  const owned: { dispose(): void }[] = [cube, flames, disc, postMaterial, flameMaterial, poolMaterial, dustMaterial];
  return {
    group,
    bake,
    plan,
    updateFog,
    update(time, depth, camera) {
      released = false;
      flameTime.value = time;
      glow.update(time, depth, camera);
      shafts.update(time, depth, camera);
    },
    release() {
      if (released) return;
      released = true;
      for (const g of [cube, flames, disc, glow.mesh.geometry]) g.dispose();
      shafts.release();
      for (const mesh of [posts, flameMesh, pools, dust, glow.mesh]) mesh.dispose();
      bake.dispose();
    },
    dispose() {
      group.removeFromParent();
      for (const mesh of [posts, flameMesh, pools, dust]) mesh.dispose();
      for (const o of owned) o.dispose();
      glow.dispose();
      shafts.dispose();
      bake.dispose();
    },
  };
};
